import React, { useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	Typography,
	Stack,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Checkbox,
	TableSortLabel
} from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import SelectDepot from './SelectDepot';
import SelectQuantity from './SelectQuantity';
import headCells from './headCells';
import utils from 'components/utils';

const DeletedOrdersTable = props => {
	const theme = useTheme();
	const translate = utils.useI18N();
	const items = useStoreState(state => state.cart.deleted.items);
	const selectAll = useStoreActions(dispatch => dispatch.cart.deleted.selectAll);
	const deSelectAll = useStoreActions(dispatch => dispatch.cart.deleted.deSelectAll);
	const toggleSelection = useStoreActions(dispatch => dispatch.cart.deleted.toggleSelection);
	const order = useStoreState(state => state.cart.deleted.orderDirection);
	const orderBy = useStoreState(state => state.cart.deleted.orderBy);
	const setOrder = useStoreActions(dispatch => dispatch.cart.deleted.setOrder);
	const selectDepotsValue = useStoreState(state => state.cart.deleted.selectDepotsValue);
	const token = useStoreState(state => state.cart.deleted.filters.token);
	const depots = useStoreState(state => state.cart.deleted.filters.depots);
	const itemSelected = items.filter(i => i.isSelected);
	const colorF2 = '#e1d3e8';
	const colorNotF2 = '#fbe4f2';
	const [sortedRows, setSortedRows] = useState([]);

	const getColorDeletedRow = causaRifiuto => {
		/* Modificato con gestione F2 / diverso da F2
		const gamma = [
			{ codice: 'F1-', color: '#fbe4f2' },
			{ codice: 'F3-', color: '#edd2ec' },
			{ codice: 'F6-', color: '#f9eef9' }
		];
		const exitsColor = gamma.filter(g => causaRifiuto && causaRifiuto.includes(g.codice));
		return exitsColor && exitsColor.length > 0 ? exitsColor[0].color : theme.palette.custom.magentalight;
		*/
		return causaRifiuto && causaRifiuto.includes('F2-') ? colorF2 : colorNotF2;
	};

	const onSelectAll = event => {
		if (event.target.checked) selectAll();
		else deSelectAll();
	};

	useEffect(() => {
		if (token.trim() === '' && selectDepotsValue.length == 0) {
			setSortedRows(utils.stableSort(items, order, orderBy));
			return;
		} else {
			let visibleItems = items;
			if (token.trim() !== '' && token.trim().length >= 3) {
				visibleItems = visibleItems.filter(
					item =>
						(item.author && item.author.includes(token)) ||
						(item.title && item.title.includes(token)) ||
						(item.publisher && item.publisher.includes(token)) ||
						(item.notes && item.notes.includes(token)) ||
						(item.depotDesc && item.depotDesc.includes(token)) ||
						(item.catalog && item.catalog.includes(token))
				);
			}
			if (depots.length > 0) {
				visibleItems = visibleItems.filter(item => depots.includes(item.depot));
			}
			setSortedRows(utils.stableSort(visibleItems, order, orderBy));
		}
	}, [items, token, selectDepotsValue, order, orderBy]);

	return (
		<Stack
			direction="column"
			justifyContent="flex-start"
			alignItems="flex-start"
			spacing={1}
			sx={{ width: '100%' }}
		>
			{/* Legenda */}
			<Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
				<Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0}>
					<SquareIcon sx={{ color: colorF2 }} />
					<Typography variant="titleTab" sx={{ textTransform: 'lowercase' }}>
						{translate('orders.expiredOrders')}
					</Typography>
				</Stack>
				<Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0}>
					<SquareIcon sx={{ color: colorNotF2 }} />
					<Typography variant="titleTab" sx={{ textTransform: 'lowercase' }}>
						{translate('orders.deletedOrders')}
					</Typography>
				</Stack>
			</Stack>

			<TableContainer sx={{ width: '100%', maxWidth: '1300px' }}>
				<Table aria-label="deleted order items table">
					<TableHead>
						<TableRow>
							<TableCell
								sx={{
									background: theme.palette.custom.greytable,
									fontWeight: '600',
									padding: '0 0 0 4px'
								}}
							>
								<Checkbox
									indeterminate={itemSelected.length > 0 && itemSelected.length < sortedRows.length}
									checked={itemSelected.length > 0 && sortedRows.length === itemSelected.length}
									onChange={onSelectAll}
									inputProps={{ 'aria-label': 'select all items' }}
								/>
							</TableCell>
							{headCells.map(headCell => (
								<TableCell
									key={headCell.id}
									align="left"
									sortDirection={orderBy === headCell.id ? order : false}
									style={headCell.sx}
									sx={{
										background: theme.palette.custom.greytable,
										fontWeight: '600',
										padding: '0 0 0 4px'
									}}
								>
									<TableSortLabel
										active={orderBy === headCell.id}
										direction={orderBy === headCell.id ? order : 'asc'}
										onClick={e => setOrder(headCell.id)}
										sx={{ lineHeight: '18px' }}
									>
										{headCell.label && translate(headCell.label)}
									</TableSortLabel>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedRows &&
							sortedRows.map((row, index) => (
								<TableRow
									key={index}
									sx={{
										backgroundColor: row.isSelected
											? theme.palette.custom.magenta
											: getColorDeletedRow(row.causaRifiuto)
									}}
								>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Checkbox
											checked={row.isSelected}
											onChange={e => toggleSelection(row.code)}
											inputProps={{ 'aria-labelledby': row.code }}
										/>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable" sx={{ textTransform: 'capitalize' }}>
											{row.author}
										</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Stack
											direction="column"
											justifyContent="flex-start"
											alignItems="flex-start"
											spacing={0}
										>
											<Typography variant="textTable" sx={{ color: theme.palette.custom.black }}>
												{row.title}
											</Typography>
											<Typography variant="textTable">{row.catalog}</Typography>
										</Stack>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable" sx={{ textTransform: 'capitalize' }}>
											{row.publisher}
										</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{utils.formatPrice(row.price)}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										{row.isSelected && <SelectDepot item={row} />}
										{!row.isSelected && (
											<Typography variant="textTable">{row.depotDesc}</Typography>
										)}
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{row.expirationDate.format('L')}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{row.code}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px', textAlign: 'center' }}>
										<Typography variant="textTable" sx={{ textAlign: 'center' }}>
											{row.isSelected ? <SelectQuantity item={row} /> : row.quantityRequired}
										</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px', textAlign: 'center' }}>
										<Typography variant="textTable">{row.quantityEvadible}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{row.notice}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{row.slaDesc}</Typography>
									</TableCell>
									<TableCell sx={{ padding: '4px 0 4px 4px' }}>
										<Typography variant="textTable">{row.notes}</Typography>
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
};

export default DeletedOrdersTable;
